import { Action } from '@reduxjs/toolkit';
import React, { RefObject, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';

import { post } from '@eika-infrastruktur/request';

import { StepNavigation } from '../components/step-navigation/step-navigation';
import { useBankregnr } from '../config';
import { useKunde } from '../kundeinfo/kunde-context';
import { isValidForCache } from '../meldskade/besvarteSporsmal/besvarteSporsmal';
import { getRequestOptions } from '../meldskade/reducer/skademelding-actions';
import { AppState } from '../reducers';
import { useRegisterSporsmalFullfort } from '../registerSporsmalFullfortContext';
import { BesvarteSporsmalStateDTO, Skademeldingstype, SporsmalType } from '../types/generated-types';
import { saveBesvarteSporsmalUrl } from '../urler';
import { assertUnreachable } from '../utils/compiler-utils';
import { SideType, Skjemaside } from '../utils/piwik-utils';
import { AttentionMessage } from './attentionMessage/attention-message';
import { Blindvei } from './blindvei/blindvei';
import { isBlindveisporsmal, isBlindveisporsmalVisibleOnSporsmalside } from './blindvei/blindvei-types';
import { Card } from './card/card';
import { Checkbox } from './checkbox/checkbox';
import { Dato } from './dato/dato';
import { DirectFilopplasting } from './filopplasting/direct-filopplasting';
import { Filopplasting } from './filopplasting/filopplasting';
import { directUploadingFile } from './filopplasting/filopplasting-types';
import { ForsikringObjekterCheckbox } from './forsikring-objekter-checkbox/forsikring-objekter-checkbox';
import { Forsikring } from './forsikring/forsikring';
import { Forsikringsnummer } from './forsikringsnummer/forsikringsnummer';
import { FocusableHtmlElement, useWindowScrollTop } from './hooks';
import { Informasjon } from './informasjon/informasjon';
import { KontaktInfoInvolvert } from './kontaktInfoInvolvert/kontaktInfoInvolvert';
import { Kontaktinfo } from './kontaktinfo/kontaktinfo';
import { Kredittkortnummer } from './kredittkortnummer/kredittkortnummer';
import ModalSporsmal from './modal/modal-sporsmal';
import { Postnummer } from './postnummer/postnummer';
import { Radio } from './radio/radio';
import {
    BesvartSporsmal,
    CurrentSporsmal,
    isCurrentSporsmalValid,
    isSporsmalSteg,
    toBesvartSporsmal,
} from './reducer/current-steg';
import { nextStepAction } from './reducer/sporsmalsflyt-actions';
import { SporsmalsflytDispatchProvider } from './reducer/sporsmalsflytDispatchProvider';
import { Select } from './select/select';
import { SimpleKontaktinfo } from './simpleKontaktinfo/simpleKontaktinfo';
import { SkadedatoAndForsikring } from './skadedato-and-forsikring/skadedato-and-forsikring';
import { SkadelidtInfo } from './skadelidtInfo/skadelidtInfo';
import { Skadenummer } from './skadenummer/skadenummer';
import { SporsmalList } from './sporsmalList/sporsmalList';
import { SporsmalSide } from './sporsmalSide/sporsmalSide';
import { Tall } from './tall/tall';
import { Tekst } from './tekst/tekst';
import { Utlegg } from './utlegg/utlegg';
import { VerkstedSporsmal } from './verksted/verksted';

interface SporsmalProps {
    currentSporsmal: CurrentSporsmal;
    showFeilmelding: boolean;
    onNext: () => void;
    focusOnMount: boolean;
    errorRef?: RefObject<FocusableHtmlElement>;
    prefix?: string;
}

export const Sporsmal = ({
    currentSporsmal,
    showFeilmelding,
    onNext,
    focusOnMount,
    errorRef,
    prefix,
}: SporsmalProps) => {
    switch (currentSporsmal.type) {
        case SporsmalType.Radio:
            return (
                <Radio
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                    prefix={prefix}
                />
            );

        case SporsmalType.Select:
            return <Select currentSporsmal={currentSporsmal} showFeilmelding={showFeilmelding} prefix={prefix} />;

        case SporsmalType.Checkbox:
            return (
                <Checkbox
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                    prefix={prefix}
                />
            );

        case SporsmalType.Forsikring:
            return <Forsikring currentSporsmal={currentSporsmal} focusOnMount={focusOnMount} errorRef={errorRef} />;

        case SporsmalType.SkadedatoAndForsikring:
            return (
                <SkadedatoAndForsikring
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Tekst:
            return (
                <Tekst
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Dato:
            return (
                <Dato
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Tall:
            return (
                <Tall
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Kontaktinfo:
            return (
                <Kontaktinfo
                    currentSporsmal={currentSporsmal}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.KontaktInfoInvolvert:
            return (
                <KontaktInfoInvolvert
                    currentSporsmal={currentSporsmal}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.SimpleKontaktinfo:
            return (
                <SimpleKontaktinfo
                    currentSporsmal={currentSporsmal}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.SkadelidtInfo:
            return (
                <SkadelidtInfo
                    currentSporsmal={currentSporsmal}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                />
            );

        case SporsmalType.Filopplasting:
            if (currentSporsmal.sporsmal.withDirectUpload) {
                return (
                    <DirectFilopplasting
                        currentSporsmal={currentSporsmal}
                        showFeilmelding={showFeilmelding}
                        focusOnMount={focusOnMount}
                        errorRef={errorRef}
                    />
                );
            }
            return (
                <Filopplasting
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Utlegg:
            return (
                <Utlegg
                    currentSporsmal={currentSporsmal}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );
        case SporsmalType.Skadenummer:
            return (
                <Skadenummer
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Forsikringsnummer:
            return (
                <Forsikringsnummer
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Verksted:
            return (
                <VerkstedSporsmal
                    currentSporsmal={currentSporsmal}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Informasjon:
            return (
                <Informasjon
                    tittel={currentSporsmal.sporsmal.tekst}
                    ingress={currentSporsmal.sporsmal.ingress}
                    svgCard={currentSporsmal.sporsmal.svgCard}
                    onNext={onNext}
                    focusOnMount={focusOnMount}
                />
            );

        case SporsmalType.SporsmalList:
            return (
                <SporsmalList
                    currentSporsmalList={currentSporsmal}
                    onNext={onNext}
                    showFeilmeldinger={showFeilmelding}
                    focusOnMount={focusOnMount}
                    addCollectionButtonLabel={currentSporsmal.sporsmal.addCollectionButtonLabel}
                    removeButtonLabel={currentSporsmal.sporsmal.removeButtonLabel}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Blindvei:
            return <Blindvei tittel={currentSporsmal.sporsmal.tekst} ingress={currentSporsmal.sporsmal.ingress} />;

        case SporsmalType.SporsmalSide:
            return (
                <SporsmalSide
                    currentSporsmal={currentSporsmal}
                    onNext={onNext}
                    showFeilmelding={showFeilmelding}
                    errorRef={errorRef}
                    key={currentSporsmal.sporsmal.id}
                />
            );
        case SporsmalType.AttentionMessage:
            return (
                <AttentionMessage
                    id={currentSporsmal.sporsmal.id}
                    type={currentSporsmal.sporsmal.attentionMessageType}
                    text={currentSporsmal.sporsmal.tekst}
                    icon={currentSporsmal.sporsmal.iconType}
                    linkItem={currentSporsmal.sporsmal.linkItem}
                    tittel={currentSporsmal.sporsmal.tittel}
                />
            );
        case SporsmalType.Card:
            return <Card currentSporsmal={currentSporsmal} />;
        case SporsmalType.Postnummer:
            return <Postnummer currentSporsmal={currentSporsmal} showFeilmelding={showFeilmelding} />;
        case SporsmalType.UtsattForgreining:
            return null;

        case SporsmalType.ForsikringObjekterCheckbox:
            return <ForsikringObjekterCheckbox currentSporsmal={currentSporsmal} showFeilmelding={showFeilmelding} />;

        case SporsmalType.Kredittkortnummer:
            return (
                <Kredittkortnummer
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    focusOnMount={focusOnMount}
                    errorRef={errorRef}
                />
            );

        case SporsmalType.Modal:
            return <ModalSporsmal id={currentSporsmal.sporsmal.id} modalType={currentSporsmal.sporsmal.modalType} />;

        default:
            return assertUnreachable(currentSporsmal);
    }
};

const toBesvarteSporsmalStateDTO = (
    kundenummer: string,
    skademeldingstype: Skademeldingstype,
    besvarteSporsmal: BesvartSporsmal[],
): BesvarteSporsmalStateDTO => {
    return {
        kundenummer,
        skademeldingstype,
        json: JSON.stringify(besvarteSporsmal),
    };
};

const saveBesvarteSporsmalToCache = (
    kundenummer: string,
    bankregnr: string,
    skadetype: Skademeldingstype,
    besvarteSporsmal: BesvartSporsmal[],
) => {
    if (isValidForCache(kundenummer, besvarteSporsmal)) {
        post(
            saveBesvarteSporsmalUrl(),
            toBesvarteSporsmalStateDTO(kundenummer, skadetype, besvarteSporsmal),
            getRequestOptions(bankregnr),
        ).then();
    }
};

interface Props {
    currentSporsmal: CurrentSporsmal;
    skjemaside: Skjemaside;
    onPrevious: () => any;
}

export const SporsmalCard = ({ currentSporsmal, skjemaside, onPrevious }: Props) => {
    const dispatch: Dispatch<Action> = useDispatch();
    const bankregnr = useBankregnr();
    const errorRef = useRef<FocusableHtmlElement>();
    const windowScrollTop = useWindowScrollTop();
    const { sporsmalsflytState, skadetype, shouldRouteKundeToFremtind } = useSelector(
        (state: AppState) => state.skademelding,
    );

    const { kundeinfo } = useKunde();
    const registerSporsmalFullfort = useRegisterSporsmalFullfort();

    const onNext = () => {
        windowScrollTop();
        dispatch(nextStepAction(kundeinfo));
        if (kundeinfo?.kundenummer && isSporsmalSteg(sporsmalsflytState.currentSteg)) {
            const currentBesvartSporsmal = toBesvartSporsmal(sporsmalsflytState.currentSteg.currentSporsmal);
            const besvarteSporsmal = [...sporsmalsflytState.besvarteSporsmal, currentBesvartSporsmal];
            saveBesvarteSporsmalToCache(kundeinfo?.kundenummer, bankregnr, skadetype, besvarteSporsmal);
        }
    };

    const [showFeilmelding, setShowFeilmelding] = useState(false);

    const nextHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        setShowFeilmelding(true);

        if (isCurrentSporsmalValid(currentSporsmal)) {
            onNext();
            setShowFeilmelding(false);
            registerSporsmalFullfort(currentSporsmal.sporsmal.id);
        } else if (errorRef.current) {
            errorRef.current.focus();
        }
    };

    return (
        <form className="meld-skade-form">
            <SporsmalsflytDispatchProvider>
                <Sporsmal
                    currentSporsmal={currentSporsmal}
                    showFeilmelding={showFeilmelding}
                    onNext={onNext}
                    errorRef={errorRef}
                    focusOnMount
                />
            </SporsmalsflytDispatchProvider>
            <StepNavigation
                bankregnr={bankregnr}
                key={currentSporsmal.sporsmal.id}
                next={{
                    onClick: nextHandler,
                    disabled: !currentSporsmal || directUploadingFile(currentSporsmal),
                    hidden:
                        isBlindveisporsmal(currentSporsmal) || isBlindveisporsmalVisibleOnSporsmalside(currentSporsmal),
                }}
                prev={{ onClick: onPrevious }}
                sideDetail={{
                    type: SideType.Skjemaside,
                    skademeldingstype: skjemaside.skademeldingstype,
                    currentSporsmalId: currentSporsmal.sporsmal.id,
                }}
                shouldRouteToFremtind={shouldRouteKundeToFremtind}
            />
        </form>
    );
};
